import type { ClinicalPhase } from "@care-by-ella/contracts";

// TES-1011 — the monthly review's phase decision, as the doctor reads it.
//
// CP-60 canon: the patient's keep-wish is decided at the next monthly review,
// WEIGHT_STABILISATION or CONTINUE_ACTIVE_TREATMENT, and published with it.
// The field is always offered (a patient in stabilisation can also be moved
// back), and required only while a keep-wish is open, so the wish cannot pass
// the review unanswered. The API enforces the same rule.
//
// Each choice says what it does for the patient, from where she stands now:
// the same phase chosen twice is a confirmation, not a new plan. Pure
// functions so the wording is testable without rendering.

export interface MonthlyPhaseContext {
  keepWishOpen: boolean;
  currentPhase: ClinicalPhase | null;
}

export function monthlyPhaseHint(
  phase: ClinicalPhase,
  { keepWishOpen, currentPhase }: MonthlyPhaseContext,
): string {
  if (phase === "weight_stabilisation") {
    if (currentPhase === "weight_stabilisation") {
      return "Fasen är redan viktstabilisering. Beslutet bekräftar den och ingen ny plan startas.";
    }
    return keepWishOpen
      ? "Patienten får ja på sitt önskemål och går över till viktstabilisering (CP-61). Ingen ny första dos registreras."
      : "Patienten går över till viktstabilisering (CP-61). Ingen ny första dos registreras.";
  }
  if (keepWishOpen) {
    return "Fasen ändras inte: patienten fortsätter i aktiv behandling. Önskemålet om att behålla vikten avslutas och läkarens kommentar till behandlingsbeslutet nedan publiceras som förklaringen till henne. Skriv där varför.";
  }
  if (currentPhase === "weight_stabilisation") {
    return "Patienten går tillbaka från viktstabilisering till aktiv behandling.";
  }
  return "Fasen är redan aktiv behandling. Beslutet bekräftar den.";
}

export function monthlyPhaseEmptyOption(keepWishOpen: boolean): string {
  return keepWishOpen ? "Välj behandlingsfas…" : "Inget fasbeslut";
}

export function monthlyPhaseEmptyHint(keepWishOpen: boolean): string {
  return keepWishOpen
    ? "Patienten önskar behålla ungefär den vikt hon har nu. Välj fas: önskemålet får inte passera månadsbedömningen obesvarat."
    : "Utan fasbeslut förblir fasen som den är. Välj bara om du vill ändra eller bekräfta den.";
}

/**
 * Why the review cannot be published yet because of the phase, or null.
 * Only an open keep-wish makes the decision required.
 */
export function phaseDecisionMissing(
  keepWishOpen: boolean,
  phase: ClinicalPhase | null,
): string | null {
  return keepWishOpen && phase === null
    ? "Välj behandlingsfas: patientens önskemål om att behålla vikten väntar på beslut"
    : null;
}
